import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  alpha,
  Skeleton,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  LogOut,
  Building2,
  Mail,
  Calendar,
  MessageCircle,
  Globe,
  Package,
  Moon,
  Sun,
  ChevronRight,
} from 'lucide-react';
import { setSelectedOrganisation } from '../../store/slices/organizationSlice';
import { setSelectedOrganizationId } from '../../store/slices/organizationContextSlice';
import { logoutUser } from '../../store/slices/authSlice';
import { toggleTheme } from '../../store/slices/themeSlice';
import { useOrganizations } from '../../hooks/organisation/useOrganizations';
import { useAuth } from '../../hooks/auth/useAuth';
import { Organisation } from '../../types/organization';
import { ROUTES } from '../../config/constants/routes';
import { LAYOUT } from '../../constants/layout';
import { AppDispatch, RootState } from '../../store';
import Notifications from '../../components/common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import { formatDate } from '../../utils/dateUtils';

const DealerAdminView = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, promoCode } = useAuth();
  const { organizations, isLoading, error } = useOrganizations();
  const { mode } = useSelector((state: RootState) => state.theme);

  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const activeOrganizations = organizations.filter(org => org.status === 'active');

  const handleSelect = (org: Organisation) => {
    dispatch(setSelectedOrganisation(org));
    dispatch(setSelectedOrganizationId(org.id));
    navigate(ROUTES.DASHBOARD);
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/');
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'rag':
        return { bg: alpha(theme.palette.info.main, 0.12), color: theme.palette.info.main };
      case 'scheduler':
        return { bg: alpha(theme.palette.warning.main, 0.12), color: theme.palette.warning.main };
      default:
        return {
          bg: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
        };
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar - matching SuperAdminSidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: LAYOUT.DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: LAYOUT.DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.paper,
            borderRight: 'none',
          },
        }}
      >
        <Box
          sx={{
            overflow: 'auto',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            px: 1.5,
            py: 2,
          }}
        >
          <List sx={{ flex: 1 }}>
            <ListItem
              sx={{
                py: 1.5,
                px: 2,
                mb: 1,
                borderRadius: 2,
                backgroundColor: `${theme.palette.primary.main}15`,
                '&:hover': {
                  backgroundColor: `${theme.palette.primary.main}08`,
                  cursor: 'pointer',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Building2 size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Organizations"
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              />
            </ListItem>
          </List>

          <List className="flex justify-center items-center">
            <ListItem
              onClick={handleThemeToggle}
              sx={{
                py: 1.5,
                px: 2,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: `${theme.palette.primary.main}08`,
                  cursor: 'pointer',
                },
              }}
              className="flex justify-center items-center"
            >
              <ListItemIcon sx={{ minWidth: 20, color: 'inherit' }}>
                {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </ListItemIcon>
            </ListItem>
            <ListItem
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: 2,
                borderRadius: 2,
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: `${theme.palette.error.main}15`,
                  cursor: 'pointer',
                },
              }}
              className="flex justify-center items-center"
            >
              <ListItemIcon sx={{ minWidth: 20, color: 'inherit' }}>
                <LogOut size={20} />
              </ListItemIcon>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${LAYOUT.DRAWER_WIDTH}px)`,
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
          {/* Page Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 4,
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Organizations
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
                {promoCode && (
                  <>
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: theme.palette.text.disabled,
                      }}
                    />
                    <Chip
                      label={promoCode}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                        letterSpacing: '0.03em',
                      }}
                    />
                  </>
                )}
              </Box>
            </Box>
            {!isLoading && !error && activeOrganizations.length > 0 && (
              <Chip
                icon={<Building2 size={14} />}
                label={`${activeOrganizations.length} Active`}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '& .MuiChip-icon': { color: theme.palette.success.main },
                }}
              />
            )}
          </Box>

          {/* Loading State */}
          {isLoading ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
              }}
            >
              {[1, 2, 3].map(i => (
                <Box key={i} sx={{ px: 3, py: 2, display: 'flex', gap: 3, alignItems: 'center' }}>
                  <Skeleton variant="text" width="20%" height={24} />
                  <Skeleton variant="text" width="15%" height={24} />
                  <Skeleton variant="text" width="25%" height={24} />
                  <Skeleton variant="text" width="15%" height={24} />
                  <Skeleton variant="text" width="20%" height={24} />
                </Box>
              ))}
            </Paper>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : activeOrganizations.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <Building2 size={32} color={theme.palette.primary.main} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                No organizations yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                Organizations assigned to your promo code will appear here. Contact your
                administrator to get started.
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell>Organization</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Website</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Features</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeOrganizations.map(org => {
                    const planColor = getPlanColor(org.planType);
                    return (
                      <TableRow
                        key={org.id}
                        onClick={() => handleSelect(org)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          },
                          '&:last-child td': { borderBottom: 0 },
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 200,
                            }}
                          >
                            {org.companyName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={org.planType?.toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: planColor.bg,
                              color: planColor.color,
                              fontWeight: 700,
                              fontSize: '0.65rem',
                              height: 20,
                              letterSpacing: '0.05em',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Mail size={14} color={theme.palette.text.secondary} />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 200,
                              }}
                            >
                              {org.client?.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {org.company?.website ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Globe size={14} color={theme.palette.text.secondary} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 180,
                                }}
                              >
                                {org.company.website}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(org.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {!org.isChatDisabled && (
                              <Chip
                                icon={<MessageCircle size={12} />}
                                label="Chat"
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 24, borderRadius: 1.5 }}
                              />
                            )}
                            {org.isBookingEnabled && (
                              <Chip
                                icon={<Calendar size={12} />}
                                label="Booking"
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 24, borderRadius: 1.5 }}
                              />
                            )}
                            {org.planType === 'rag' && (
                              <Chip
                                icon={<Package size={12} />}
                                label="RAG"
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 24, borderRadius: 1.5 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              color: theme.palette.text.secondary,
                              transition: 'color 0.2s ease',
                              'tr:hover &': {
                                color: theme.palette.primary.main,
                              },
                            }}
                          >
                            <ChevronRight size={18} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default DealerAdminView;
